import { format } from "./i18n.js";
import { displayTimezone, slotLocalDate } from "./timezone.js";

let flatpickrLoader;

export function syncCalendarPicker(root, state, config, handlers, messages) {
  cleanupCalendarPicker(root);

  const input = root.querySelector?.("[data-rdv-calendar-input]");
  if (!input) {
    return;
  }

  // Jours disponibles dans le fuseau d'affichage (un créneau tardif peut
  // tomber un autre jour selon le fuseau du visiteur).
  const tz = displayTimezone(state, config);
  const localDate = (slot) => (tz && slotLocalDate(slot.start, tz)) || slot.date;
  const availableDates = [...new Set(state.slots.map(localDate))].sort();
  if (availableDates.length === 0 || state.status === "loading" || state.status === "success") {
    return;
  }

  const selectionToken = Symbol("rdv-calendar");
  root.__rdvCalendarToken = selectionToken;

  void loadFlatpickr().then(({ flatpickr, locales }) => {
    if (root.__rdvCalendarToken !== selectionToken) {
      return;
    }

    const slotCounts = countSlotsByDate(state.slots, localDate);
    const minDate = availableDates[0];
    const maxDate = availableDates[availableDates.length - 1];
    const instance = flatpickr(input, {
      inline: true,
      disableMobile: true,
      locale: locales[messages.lang] || locales.fr,
      dateFormat: "Y-m-d",
      defaultDate: state.selectedDate || undefined,
      enable: availableDates,
      minDate,
      maxDate,
      monthSelectorType: "static",
      nextArrow: "›",
      prevArrow: "‹",
      onReady: (_d, _s, picker) => showYearAsText(picker),
      onMonthChange: (_d, _s, picker) => showYearAsText(picker),
      onYearChange: (_d, _s, picker) => showYearAsText(picker),
      onChange: (_selectedDates, dateStr) => {
        if (dateStr) {
          handlers.onSelectDate(dateStr);
        }
      },
      onDayCreate: (_dateObj, _dateStr, picker, dayElement) => {
        const dateKey = picker.formatDate(dayElement.dateObj, "Y-m-d");
        const count = slotCounts.get(dateKey);

        if (!count) {
          if (!dayElement.classList.contains("prevMonthDay") && !dayElement.classList.contains("nextMonthDay")) {
            dayElement.classList.add("rdv-day-unavailable");
            dayElement.setAttribute("aria-label", `${dayElement.getAttribute("aria-label") || dateKey}. ${messages.emptyDate}`);
          }
          return;
        }

        dayElement.classList.add("rdv-day-has-slots");
        dayElement.setAttribute("aria-label", `${dayElement.getAttribute("aria-label") || dateKey}. ${format(count > 1 ? messages.daySlotsMany : messages.daySlotsOne, { count })}`);

        // Compteur visible facultatif (data-show-day-count="false" pour le
        // masquer) ; le nombre reste annoncé aux lecteurs d'écran (aria-label).
        if (config.showDayCount === false) {
          return;
        }
        dayElement.classList.add("rdv-day-counted");
        const marker = document.createElement("span");
        marker.className = "rdv-day-marker";
        marker.textContent = String(count);
        marker.setAttribute("aria-hidden", "true");
        dayElement.appendChild(marker);
      }
    });

    root.__rdvCalendarInstance = instance;
  });
}

// L'année n'est pas un sélecteur : flatpickr fournit un champ numérique
// (flèches natives, rendu variable selon le navigateur — Firefox garde ses
// flèches même avec appearance:none). On masque ce champ et on affiche
// l'année dans un simple texte, mis à jour à chaque changement de mois.
export function showYearAsText(picker) {
  for (const yearInput of picker.yearElements || []) {
    const wrapper = yearInput.parentNode;
    if (!wrapper) {
      continue;
    }
    wrapper.hidden = true;
    wrapper.setAttribute("aria-hidden", "true");
    yearInput.tabIndex = -1;
    let label = wrapper.parentNode?.querySelector(".rdv-cur-year");
    if (!label) {
      label = wrapper.ownerDocument.createElement("span");
      label.className = "rdv-cur-year";
      wrapper.after(label);
    }
    label.textContent = String(picker.currentYear);
  }
}

export function cleanupCalendarPicker(root) {
  root.__rdvCalendarToken = null;

  if (root.__rdvCalendarInstance) {
    root.__rdvCalendarInstance.destroy();
    root.__rdvCalendarInstance = null;
  }
}

function loadFlatpickr() {
  flatpickrLoader ??= Promise.all([
    import("flatpickr"),
    import("flatpickr/dist/l10n/fr.js"),
    import("flatpickr/dist/l10n/es.js")
  ]).then(([flatpickrModule, frModule, esModule]) => ({
    flatpickr: flatpickrModule.default,
    // L'anglais est la langue native de flatpickr : seule la semaine est
    // ajustée pour commencer le lundi, comme en français et en espagnol.
    locales: { fr: frModule.French, es: esModule.Spanish, en: { firstDayOfWeek: 1 } }
  }));

  return flatpickrLoader;
}

function countSlotsByDate(slots, localDate) {
  return slots.reduce((counts, slot) => {
    const key = localDate(slot);
    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());
}